import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { SageStatusBadge } from "@/components/SageStatusBadge";
import { Plug, RefreshCw, RotateCcw, ClipboardCheck, Loader2, Server, Download, Copy, Cable, Tag, ReceiptText } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IE", { timeZone: "Europe/Dublin" }) : "—");

export default function SageIntegration() {
  const [status, setStatus] = useState(null);
  const [gw, setGw] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/sage/status").then(({ data }) => setStatus(data));
  useEffect(() => {
    load();
    api.get("/admin/sage/gateway-info").then(({ data }) => setGw(data));
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const setMode = async (mode) => {
    try { await api.post("/admin/sage/mode", { mode }); toast.success(`Mode set to ${mode}`); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const { data } = await fn(); toast.success(msg(data)); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const simulate = async (s) => {
    await api.post("/admin/sage/simulate", { status: s });
    toast.info(s ? `Simulating ${s}` : "Auto status restored");
    await load();
  };

  const metrics = status ? [
    ["Last Communication", fmt(status.last_communication)],
    ["Last Stock Update", fmt(status.last_stock_update)],
    ["Last Price Update", fmt(status.last_price_update)],
    ["Last Invoice Update", fmt(status.last_invoice_update)],
    ["Last Order Export", fmt(status.last_order_export)],
    ["Last Customer Sync", fmt(status.last_customer_sync)],
    ["Pending Orders", status.pending_orders],
    ["Failed Jobs", status.failed_jobs],
    ["Stock Discrepancies", status.stock_discrepancies],
  ] : [];

  const actions = [
    { key: "test", label: "Test Connection", icon: Plug, fn: () => api.post("/admin/sage/test-connection"), msg: (d) => d.message },
    { key: "sync", label: "Sync Stock Now", icon: RefreshCw, fn: () => api.post("/admin/sage/sync-stock"), msg: (d) => `Synced ${d.updated} SKUs` },
    { key: "prices", label: "Sync Prices Now", icon: Tag, fn: () => api.post("/admin/sage/sync-prices"), msg: (d) => `${d.changed} price change(s) across ${d.checked} SKUs` },
    { key: "invoices", label: "Pull Invoices", icon: ReceiptText, fn: () => api.post("/admin/sage/pull-invoices"), msg: (d) => d.message || `${d.updated} invoice(s) pulled from Sage` },
    { key: "retry", label: "Retry Failed Orders", icon: RotateCcw, fn: () => api.post("/admin/sage/retry-failed"), msg: (d) => `Retried ${d.retried} orders` },
    { key: "recon", label: "Run Full Reconciliation", icon: ClipboardCheck, fn: () => api.post("/admin/reconciliations/run"), msg: () => "Reconciliation complete" },
  ];

  return (
    <div>
      <h1 className="font-serif-display text-3xl font-bold text-slate-50">Sage 50 Integration</h1>
      <p className="mt-1 text-sm text-[#94A3B8]">M&C Sage Integration Gateway — real-time stock master. The frontend never talks to Sage directly.</p>

      {/* Connection mode + on-prem gateway wiring */}
      <div className="mt-6 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#D4AF37]/10"><Cable className="h-6 w-6 text-[#D4AF37]" /></div>
            <div>
              <div className="text-sm text-[#64748B]">Connection Mode</div>
              <div className="flex items-center gap-2">
                <span data-testid="sage-mode" className={`rounded-full border px-3 py-1 text-xs font-bold ${status?.mode === "REAL" ? "border-emerald-500/40 text-emerald-400" : "border-sky-500/40 text-sky-400"}`}>{status?.mode || "…"}</span>
                {status?.mode === "REAL" && <span className="text-xs text-[#94A3B8]">Sage 50c {status?.gateway_version} · last seen {fmt(status?.gateway_last_seen)}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#64748B]">Mode:</span>
            <button data-testid="mode-real" onClick={() => setMode("REAL")} className="rounded-full border border-[#2A2E39] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-emerald-500/50 hover:text-emerald-400">REAL (live Sage)</button>
            <button data-testid="mode-sim" onClick={() => setMode("SIMULATED")} className="rounded-full border border-[#2A2E39] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-sky-500/50 hover:text-sky-400">SIMULATED (demo)</button>
          </div>
        </div>

        {gw && (
          <div className="mt-6 space-y-3">
            <div className="flex items-start gap-3 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4 text-sm text-[#B6BECD]">
              <Server className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
              Install the agent on the Windows PC running Sage 50c. It pushes live stock and collects web orders over HTTPS using the key below. Sage/SDO credentials stay on that machine.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
                <div className="text-xs text-[#64748B]">Gateway API Base</div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate font-mono-data text-xs text-slate-100">{gw.api_base}</code>
                  <button data-testid="copy-api-base" onClick={() => copy(gw.api_base)} className="text-[#94A3B8] hover:text-[#D4AF37]"><Copy className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
                <div className="text-xs text-[#64748B]">Gateway API Key (X-Gateway-Key)</div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate font-mono-data text-xs text-[#D4AF37]">{gw.api_key}</code>
                  <button data-testid="copy-api-key" onClick={() => copy(gw.api_key)} className="text-[#94A3B8] hover:text-[#D4AF37]"><Copy className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
            <a data-testid="download-agent" href={`${API}/admin/sage/agent-reference`}
              className="inline-flex items-center gap-2 rounded-full border border-[#2A2E39] px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
              <Download className="h-4 w-4" /> Download Reference Agent (Python + SDO v34)
            </a>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#D4AF37]/10"><Plug className="h-6 w-6 text-[#D4AF37]" /></div>
            <div>
              <div className="text-sm text-[#64748B]">Gateway Status</div>
              {status ? <SageStatusBadge status={status.status} size="lg" testId="admin-sage-status-indicator" /> : <Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#64748B]">Simulate:</span>
            {["CONNECTED", "DEGRADED", "DISCONNECTED"].map((s) => (
              <button key={s} data-testid={`sage-sim-${s.toLowerCase()}`} onClick={() => simulate(s)}
                className="rounded-full border border-[#2A2E39] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">{s}</button>
            ))}
            <button data-testid="sage-sim-auto" onClick={() => simulate(null)} className="rounded-full border border-[#2A2E39] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#D4AF37]/50">AUTO</button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {metrics.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
              <div className="text-xs text-[#64748B]">{k}</div>
              <div className="mt-1 font-mono-data text-sm font-semibold text-slate-100">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map((a) => (
          <button key={a.key} data-testid={`admin-${a.key}-btn`} disabled={!!busy} onClick={() => run(a.key, a.fn, a.msg)}
            className="group flex flex-col items-start gap-3 rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-5 text-left transition-colors hover:border-[#D4AF37]/50 disabled:opacity-60">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]">
              {busy === a.key ? <Loader2 className="h-5 w-5 animate-spin" /> : <a.icon className="h-5 w-5" />}
            </div>
            <span className="text-sm font-semibold text-slate-100">{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
